import React from "react";
import { Link } from "react-router-dom";
import { usePageBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import { PageHeader, KpiTile, SectionCard, StatePill, Pill } from "../../components/Ui";
import { Receipt, ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import DashboardAlertBanner from "../../components/dashboard/DashboardAlertBanner";
import { SettlementStatusBadge } from "../../components/OutwardSettlementPanel";

function salesQueueLabel(d) {
  const bucket = d?.voucher_bucket;
  const status = String(d?.sales_settlement_status || "").toUpperCase();
  if (bucket === "dispatch" || !d?.sales_voucher_id) return "At dispatch";
  if (status === "PROVISIONAL" || bucket === "provisional") return "Provisional";
  return status || "Pending";
}

function tallyLabel(status) {
  const s = String(status || "NOT_SYNCED").toUpperCase();
  if (s === "SYNCED") return null;
  return s.replace(/_/g, " ");
}

export default function AccountsDashboard() {
  const { data, reload } = usePageBootstrap({
    path: "/dashboard/bootstrap",
    wsTypes: ["purchase", "outward_dispatch", "voucher"],
  });
  useBootstrapWS(["purchase", "outward_dispatch", "voucher"], reload);

  const accountsPending = data?.pending_preview || [];
  const sales = data?.sales || {};
  const agg = data?.aggregates || {};
  const salesQueue = sales.queue_preview || [];

  const fmt = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="accounts-dashboard">
      <PageHeader
        eyebrow="Accounts & Finance"
        title="Accounts Desk"
        description="Purchase and sales settlement in one place — finalize trucks, then post to Tally."
        actions={
          <Button asChild className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-11">
            <Link to="/accounts">
              <Receipt className="h-4 w-4 mr-2" />
              Open accounts
            </Link>
          </Button>
        }
      />

      {(agg.pravesh_parchi_pending_count ?? 0) > 0 && (
        <DashboardAlertBanner
          testId="acct-parchi-alert"
          tone="amber"
          title={`${agg.pravesh_parchi_pending_count} supplier truck(s) missing pravesh parchi`}
          description="Finalize only after parchi is on file, or filter them in purchase review."
          href="/accounts"
          hrefLabel="Purchase review"
        />
      )}

      {(sales.adjustment_journal_pending_count ?? 0) > 0 && (
        <DashboardAlertBanner
          testId="acct-sales-journal-alert"
          tone="teal"
          title={`${sales.adjustment_journal_pending_count} sales adjustment journal(s) not in Tally`}
          description="Final settlement is done — post the delta journal against the provisional invoice."
          href="/sales/vouchers"
          hrefLabel="Sales vouchers"
        />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <KpiTile
          label="Purchase pending"
          value={data?.pending_count ?? 0}
          sub="Awaiting finalize"
          accent
          testId="acct-kpi-purchase-pending"
        />
        <KpiTile
          label="Sales @ dispatch"
          value={sales.at_dispatch_count ?? 0}
          sub="Issue provisional"
          testId="acct-kpi-sales-dispatch"
        />
        <KpiTile
          label="Sales provisional"
          value={sales.provisional_count ?? 0}
          sub="Final settlement"
          testId="acct-kpi-sales-provisional"
        />
        <KpiTile
          label="Tally pending"
          value={(sales.tally_pending_count ?? 0) + (sales.adjustment_journal_pending_count ?? 0)}
          sub="Sales + journals"
          testId="acct-kpi-tally"
        />
        <KpiTile label="Cash queue" value={agg.cash_count ?? 0} sub="Cash ledger" testId="acct-kpi-cash" />
        <KpiTile label="Bank queue" value={agg.receipt_count ?? 0} sub="BP / BR" testId="acct-kpi-bank" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <SectionCard title="Purchase — awaiting review" testId="acct-purchase-queue">
          {accountsPending.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No purchases pending accounts review.</p>
          ) : (
            <div className="space-y-2">
              {accountsPending.slice(0, 8).map((p) => (
                <Link
                  key={p.id}
                  to="/accounts"
                  className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-3 hover:bg-muted/40 transition-colors"
                  data-testid={`acct-pending-${p.code}`}
                >
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-foreground">{p.code}</div>
                    <div className="text-sm truncate text-muted-foreground">{p.party_name}</div>
                  </div>
                  <StatePill state={p.state} />
                </Link>
              ))}
            </div>
          )}
          <Link to="/accounts" className="inline-flex items-center gap-1 text-xs link-brand mt-3">
            Open purchase settlement <ArrowRight className="h-3 w-3" />
          </Link>
        </SectionCard>

        <SectionCard title="Sales — awaiting action" testId="acct-sales-queue">
          {salesQueue.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No outward trucks in accounts queue.</p>
          ) : (
            <div className="space-y-2">
              {salesQueue.map((d) => {
                const tally = tallyLabel(d.sales_tally_sync_status || d.tally_sync_status);
                return (
                  <Link
                    key={d.id}
                    to={`/sales/vouchers?dispatch=${d.id}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-3 hover:bg-muted/40 transition-colors"
                    data-testid={`acct-sales-${d.code}`}
                  >
                    <div className="min-w-0">
                      <div className="font-mono text-sm text-foreground">{d.code}</div>
                      <div className="text-sm truncate text-muted-foreground">
                        {d.party_name}
                        {d.vehicle_number ? ` · ${d.vehicle_number}` : ""}
                      </div>
                      {d.sales_voucher_code ? (
                        <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{d.sales_voucher_code}</div>
                      ) : null}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {d.sales_voucher_id ? (
                        <SettlementStatusBadge status={d.sales_settlement_status} />
                      ) : (
                        <Pill tone="gold">{salesQueueLabel(d)}</Pill>
                      )}
                      {tally ? <Pill tone="warning">{tally}</Pill> : null}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
          <Link to="/sales/vouchers" className="inline-flex items-center gap-1 text-xs link-brand mt-3">
            Open sales vouchers <ArrowRight className="h-3 w-3" />
          </Link>
        </SectionCard>
      </div>

      <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
        <span>
          Today: <span className="font-medium text-foreground tabular">{data?.completed_today ?? 0}</span> vouchers
        </span>
        <span>
          Payables: <span className="font-medium text-foreground tabular">{fmt(agg.outstanding_payments)}</span>
        </span>
        <span>
          Receivables: <span className="font-medium text-foreground tabular">{fmt(agg.total_receivable)}</span>
        </span>
      </div>
    </div>
  );
}
